import React from 'react';

export const GradientDefs = () => (
  <svg style={{ height: 0 }}>
    <defs>
      {/* 1. Gradient 308.09deg */}
      <linearGradient id="gradientId0" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="15%" stopColor="#FFD34E" />
        <stop offset="77%" stopColor="#FF870A" /> 
      </linearGradient>

      {/* 2. Gradient 308.09deg */}
      <linearGradient id="gradientId1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#16ECD1" />
        <stop offset="100%" stopColor="#067365" />
      </linearGradient>

      {/* 3. Gradient 308.93deg */}
      <linearGradient id="gradientId2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="15.14%" stopColor="#4AC7FF" />
        <stop offset="117.27%" stopColor="#166EFF" />
      </linearGradient>
      <linearGradient id="gradientId2Light" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="15.14%" stopColor="#BEE6FF" />
        <stop offset="117.27%" stopColor="#8FB8FF" />
      </linearGradient>

      {/* 4. Gradient 102.13deg */}
      <linearGradient id="gradientId3" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="26.28%" stopColor="#A9A5FF" />
        <stop offset="87.7%" stopColor="#685FBA" />
      </linearGradient>
      <linearGradient id="gradientId3Light" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="26.28%" stopColor="#DEDCFF" />
        <stop offset="87.7%" stopColor="#B7B2E0" />
      </linearGradient>

      {/* 5. Gradient 121.05deg */}
      <linearGradient id="gradientId4" x1="20%" y1="100%" x2="100%" y2="0%">
        <stop offset="17.81%" stopColor="#EB419E" />
        <stop offset="80.51%" stopColor="#7D1F78" />
      </linearGradient>
      <linearGradient id="gradientId4Light" x1="20%" y1="100%" x2="100%" y2="0%">
        <stop offset="17.81%" stopColor="#F7B8DC" />
        <stop offset="80.51%" stopColor="#CB94C7" />
      </linearGradient>

      {/* 7. Gray Gradient for Omitted Metrics */}
      <linearGradient id="gradientId5" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#B8B8B8" />
        <stop offset="100%" stopColor="#E0E0E0" />
      </linearGradient>
      <linearGradient id="gradientId5Light" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#D8D8D8" />
        <stop offset="100%" stopColor="#EFEFEF" />
      </linearGradient>
    </defs>
  </svg>
);

export default GradientDefs;

/**
 * Returns a safe, bounds-checked MetricColors index.
 * Omitted metrics always use index 5 (gray).
 * All other indices wrap around via modulo so they never exceed the array length.
 */
export const safeMetricColorIndex = (index: number, omitted?: boolean): number =>
    omitted ? 5 : (index ?? 0) % MetricColors.length;

export const MetricColors = [
  {
    id: 0,
    metricNotMet: '#B14F2F',
    metricMet: '#FF9933',
    textColor:'#B14F2F',
    bgColor: '#FFECD3',
    barTextColor: '#FFFFFF',
  },
  {
    id: 1,
    metricNotMet: '#067365',
    metricMet: '#16ECD1',
    textColor: '#067365',
    bgColor: '#E8FBF9',
    barTextColor: '#FFFFFF',
  },
  {
    id: 2,
    metricNotMet: '#2B5B93',
    metricMet: '#3299FF',
    textColor: '#2B5B93',
    bgColor: '#CFEAFF',
    barTextColor: '#FFFFFF',
  },
  {
    id: 3,
    metricNotMet: '#685FBA',
    metricMet: '#A9A5FF',
    textColor: '#685FBA',
    bgColor: '#E0E0FF',
    barTextColor: '#FFFFFF',
  },
  {
    id: 4,
    metricNotMet: '#7D1F78',
    metricMet: '#EB419E',
    textColor:'#7D1F78',
    bgColor:'#EFCBF1',
    barTextColor: '#FFFFFF',
  },
  {
    id: 5,
    metricNotMet: '#CECECD',
    metricMet: '#3F5765',
    textColor: '#3F5765',
    bgColor: '#ECECEC',
    barTextColor: '#757575',
  }
];

/**
 * Solid (non-gradient) color triples matching the tiered gauge's 3 stacked reference arcs:
 * - reference: bright/track color, always the full 0-100% arc (bottom layer)
 * - light: Exceeds bar color (middle layer)
 * - dark: Meets bar color (top layer)
 * Confirmed exact from Figma export for indices 0/1/2 (FCR/Resolved %/Quality). Indices 3/4
 * (Tickets/Complaints) only had 2 distinct solid fills in the source export - "reference" for
 * those is approximated (lightened from "light") rather than pixel-matched; flag if you get a
 * reference screenshot showing their true reference-arc color.
 */
export const TieredMetricColors = [
  { reference: '#FF9933', dark: '#B14F2F', light: '#E26A43' }, // 0 - rust (FCR, exact)
  { reference: '#0CD9BF', dark: '#089886', light: '#00B6B2' }, // 1 - teal (Resolved %, exact)
  { reference: '#3299FF', dark: '#2B5B93', light: '#3B81D2' }, // 2 - blue (Quality 2.0, exact)
  { reference: '#B48EE0', dark: '#601AA1', light: '#7F30C8' }, // 3 - purple (Tickets, reference approximated)
  { reference: '#E29BDD', dark: '#7D1F78', light: '#C848C2' }, // 4 - magenta (Complaints, reference approximated)
  { reference: '#E0E0E0', dark: '#8A8A8A', light: '#CCCCCC' }, // 5 - omitted
];

export const MetricTrendTierColors = [
  { belowMeets: '#B14F2F', meets: '#E26A43', exceeds: '#FF9933' }, // 0 - rust (FCR)
  { belowMeets: '#089886', meets: '#00B6B2', exceeds: '#0CD9BF' }, // 1 - teal (Resolved %)
  { belowMeets: '#2B5B93', meets: '#2A78D3', exceeds: '#499DFF' }, // 2 - blue (Quality 2.0)
  { belowMeets: '#601AA1', meets: '#8913CD', exceeds: '#AC53FF' }, // 3 - purple (Tickets)
  { belowMeets: '#7D1F78', meets: '#C848C2', exceeds: '#EB6BE5' }, // 4 - magenta (Complaints)
  { belowMeets: '#C11E52', meets: '#E83683', exceeds: '#FF568B' }, // 5 - rose (6th real metric theme)
  { belowMeets: '#414141', meets: '#5C5C5C', exceeds: '#A3A3A3' }, // 6 - omitted (gray)
];

/**
 * Flat (non-alpha) background fills for the three zones the Meets/Exceeds reference lines
 * create on a metric trend chart, one set per metric color index (same 0-5 order as
 * MetricColors / TieredMetricColors above). From Figma's "Graphic Background Colors" swatch:
 * - start:          axis floor   -> Meets line    (darkest of the three - "starting to meets")
 * - meetsToExceeds: Meets line   -> Exceeds line   (mid shade)
 * - beyondExceeds:  Exceeds line -> axis ceiling   (lightest - "the remaining space")
 * These are exact flat colors, not alpha-blended tints of the Meets/Exceeds line colors, so use
 * them directly as dataset backgroundColor rather than running them through withAlpha().
 * This array is only ever read in tiered-mode chart code (see MetricTrendTierColors comment
 * above). Index 5 is the swatch's real 6th (rose) metric theme; index 6 is gray for omitted
 * metrics, added on top rather than overwriting index 5 - see MetricTrendTierColors for why.
 */
export const MetricZoneBackgroundColors = [
  { start: '#E0AE92', meetsToExceeds: '#F3B89A', beyondExceeds: '#FFDCB3' }, // 0 - rust/orange (FCR)
  { start: '#91D6CE', meetsToExceeds: '#8EE2DF', beyondExceeds: '#BFF7F0' }, // 1 - teal (Resolved %)
  { start: '#8DB0D4', meetsToExceeds: '#8DBCED', beyondExceeds: '#B5DAFF' }, // 2 - blue (Quality 2.0)
  { start: '#B792D9', meetsToExceeds: '#C890EB', beyondExceeds: '#E3C7FF' }, // 3 - purple (Tickets)
  { start: '#CA96C9', meetsToExceeds: '#E8A7E7', beyondExceeds: '#FACDFA' }, // 4 - magenta (Complaints)
  { start: '#E697B1', meetsToExceeds: '#F6A1C4', beyondExceeds: '#FAC5DA' }, // 5 - rose (6th real metric theme)
  { start: '#989898', meetsToExceeds: '#C1C1C1', beyondExceeds: '#DADADA' }, // 6 - omitted (gray)
];

export const OmittedMetricColor ='rgba(63, 87, 101, 1)';
